import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Settings } from '../Settings';
export function isSupportedBrowser() {
    return !/firefox/i.test(window.navigator.userAgent);
}
// Check if the browser supports even one of the video codecs we use
export async function hasSupportedVideoCodec() {
    for (const codec of Settings.videoEncoderCodecs) {
        const config = {
            codec: codec,
            width: Settings.videoEncoderDefaultWidth,
            height: Settings.videoEncoderDefaultHeight,
            hardwareAcceleration: 'prefer-hardware',
            bitrate: Settings.videoEncoderBitrateSd,
            latencyMode: 'realtime',
        };
        try {
            if (await VideoEncoder.isConfigSupported(config)) {
                return true;
            }
        }
        catch (error) {
            console.error('Error checking video codec support', error);
        }
    }
    return false;
}
export function useHasSupportedVideoCodec() {
    const [supported, setSupported] = useState(undefined);
    useEffect(() => {
        const checkVideoCodecSupport = async () => {
            const supported = await hasSupportedVideoCodec();
            setSupported(supported);
        };
        checkVideoCodecSupport();
    }, []);
    return supported;
}
export function useShare() {
    const [canShare, setCanShare] = useState(false);
    useEffect(() => {
        setCanShare(typeof navigator.share === 'function');
    }, []);
    const openSystemShareDialog = useCallback((url, options = {}) => {
        if (!canShare) {
            return;
        }
        (async () => {
            try {
                const { title = 'Watch my stream', text = 'Check out my live stream!', } = options;
                await navigator.share({
                    title,
                    text,
                    url,
                });
            }
            catch (error) {
                if (error instanceof Error) {
                    switch (error.name) {
                        case 'AbortError':
                        case 'InvalidStateError':
                            return;
                    }
                }
                console.error('Error sharing', error);
            }
        })();
    }, [canShare]);
    return {
        canShare,
        openSystemShareDialog,
    };
}
export function copy(text, options = {}) {
    (async () => {
        try {
            await navigator.clipboard.writeText(text);
            options.onDone?.();
        }
        catch (error) {
            console.error('Failed to copy URL:', error);
        }
    })();
}
/**
 * Keeps `--vw` and `--vh` up-to-date.
 *
 * These CSS variables are calculated from `window.innerWidth`/`.innerHeight`
 * for better control over mobile viewports.
 *
 * You can also use `var(--vw-100)` for `100vw` & `var(--vh-100)` for `100vh`.
 */
export function useViewportSizeUpdaterEffect() {
    useLayoutEffect(function assignSizeCSSVariables() {
        function onResize() {
            const { innerWidth: width, innerHeight: height } = window;
            document.documentElement.style.setProperty('--vw', `${width * 0.01}px`);
            document.documentElement.style.setProperty('--vh', `${height * 0.01}px`);
        }
        onResize();
        window.addEventListener('resize', onResize);
        window.visualViewport?.addEventListener('resize', onResize);
        return () => {
            window.visualViewport?.removeEventListener('resize', onResize);
            window.removeEventListener('resize', onResize);
        };
    }, []);
}
/**
 * Returns current screen orientation.
 */
export function useScreenOrientation() {
    const [screenOrientation, setScreenOrientation] = useState(() => screen.orientation.type);
    useEffect(() => {
        function onScreenOrientationChange() {
            setScreenOrientation(screen.orientation.type);
        }
        screen.orientation.addEventListener('change', onScreenOrientationChange);
        return () => {
            screen.orientation.removeEventListener('change', onScreenOrientationChange);
        };
    }, []);
    return screenOrientation;
}
/**
 * Extends the given video properties object by infering rotation details.
 */
export function getEnhancedVideoProperties(videoProperties) {
    const { screenOrientationAngle, videoOrientationAngle, width, height, type, } = videoProperties;
    const rotationAngle = (screenOrientationAngle + videoOrientationAngle) % 360;
    const rad = (rotationAngle * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rad));
    const cos = Math.abs(Math.cos(rad));
    const rotatedWidth = width * cos + height * sin;
    const rotatedHeight = height * cos + width * sin;
    return {
        ...videoProperties,
        rotatedWidth,
        rotatedHeight,
        rotationAngle,
        preferredOrientation: type === 'screen'
            ? undefined
            : rotatedWidth > rotatedHeight
                ? 'landscape'
                : 'portrait',
    };
}
/**
 * Calculates the angle (in °) between user's screen orientation and the
 * preferred video orientation.
 */
export function getPreferredOrientationAdjustmentAngle(orientation, screenOrientation) {
    return orientation && !screenOrientation.includes(orientation)
        ? /primary/.test(screenOrientation)
            ? 90
            : -90
        : 0;
}
/**
 * Gives `meta[name=viewport]` a content value that makes the viewport non-zoomable
 * on mount. Reverts to the previous value on unmount.
 */
export function useNonScalableViewport() {
    useEffect(() => {
        let meta = document.querySelector('meta[name="viewport"]');
        const newContent = 'width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no';
        if (meta) {
            const previousContent = meta.content;
            meta.content = newContent;
            return () => {
                if (meta) {
                    meta.content = previousContent;
                }
            };
        }
        meta = document.createElement('meta');
        meta.name = 'viewport';
        meta.content = newContent;
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, []);
}
